import { Link } from '@tanstack/react-router';
import { Anchor, Bell, Car, Clock, Gavel, Receipt, Wallet } from 'lucide-react';
import type { ReactNode } from 'react';

import type { Notification } from '@/api';
import { cn } from '@/lib/utils';

/** «5m ago» / «2h ago» / дата. Сповіщення читають «щойно», тому відносний час. */
function ago(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const min = Math.round(diff / 60_000);
	if (min < 1) return 'just now';
	if (min < 60) return `${min}m ago`;
	const h = Math.round(min / 60);
	if (h < 24) return `${h}h ago`;
	return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const ICON: Record<Notification['kind'], ReactNode> = {
	BID_PLACED: <Gavel className="size-4" />,
	BID_AMOUNT_CHANGED: <Gavel className="size-4" />,
	BID_CHANGE_REQUESTED: <Clock className="size-4" />,
	BID_CHANGE_RESOLVED: <Gavel className="size-4" />,
	BID_STATUS_CHANGED: <Bell className="size-4" />,
	BALANCE_UPDATED: <Wallet className="size-4" />,
	INVOICE_CREATED: <Receipt className="size-4" />,
	PAYMENT_SUBMITTED: <Receipt className="size-4" />,
	PAYMENT_CONFIRMED: <Receipt className="size-4" />,
	VEHICLE_STATUS_CHANGED: <Car className="size-4" />,
	DEPOSIT_SUBMITTED: <Wallet className="size-4" />,
	DEPOSIT_DECLINED: <Wallet className="size-4" />,
	DESTINATION_REMOVED: <Anchor className="size-4" />,
};

const FUNDS_KINDS: Notification['kind'][] = ['BALANCE_UPDATED', 'DEPOSIT_SUBMITTED', 'DEPOSIT_DECLINED'];

/**
 * Група My Bids з адреси, яку сповіщенню дав бекенд (`/bids?group=action`):
 * «You have been outbid» веде в Needs action, «Request approved» — в Active
 * Bids. Невідому групу маршрут відкине сам (`validateSearch`).
 */
function bidsGroupOf(path: string | null): string | null {
	if (!path?.startsWith('/bids')) return null;
	return new URLSearchParams(path.split('?')[1] ?? '').get('group');
}

/**
 * Клік веде до предмета сповіщення й позначає його прочитаним (спека §9).
 * Авто → його картка; гроші (баланс, поповнення) → Payments, де депозит і
 * історія поповнень; прибраний порт → Profile, де вибирають інший; решта
 * ставок → My Bids.
 */
export function NotificationItem({
	notification,
	onOpen,
	compact = false,
}: {
	notification: Notification;
	onOpen: (id: string) => void;
	compact?: boolean;
}): ReactNode {
	const unread = notification.readAt === null;
	const money = FUNDS_KINDS.includes(notification.kind);
	const group = bidsGroupOf(notification.path);
	const target = notification.vehicleId
		? { to: '/vehicles/$vehicleId' as const, params: { vehicleId: notification.vehicleId } }
		: money
			? { to: '/payments' as const, params: {} }
			: notification.kind === 'DESTINATION_REMOVED'
				? { to: '/profile' as const, params: {} }
				: { to: '/bids' as const, params: {}, search: group ? { group } : {} };
	return (
		<Link
			{...target}
			onClick={() => onOpen(notification.id)}
			className={cn(
				'flex gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-surface-muted',
				unread && 'bg-primary-subtle/60',
			)}
		>
			<span
				className={cn(
					'mt-0.5 grid size-8 shrink-0 place-items-center rounded-full',
					unread ? 'bg-primary-solid text-primary-fg' : 'bg-surface-muted text-fg-subtle',
				)}
			>
				{ICON[notification.kind]}
			</span>
			<span className="min-w-0 flex-1">
				<span className="flex items-baseline justify-between gap-3">
					<span className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
						{notification.title}
					</span>
					<span className="shrink-0 text-[11px] text-fg-subtle">{ago(notification.createdAt)}</span>
				</span>
				<span className={cn('mt-0.5 block text-xs leading-relaxed text-fg-muted', compact && 'line-clamp-2')}>
					{notification.body}
				</span>
			</span>
		</Link>
	);
}
